import { Card, CardContent, Typography, Box, Skeleton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import { PIN_STATUS_COLORS, formatNumber } from "../constants";

interface InventoryHealthChartProps {
  data: any;
  loading: boolean;
}

const InventoryHealthChart = ({ data, loading }: InventoryHealthChartProps) => {
  if (loading) {
    return (
      <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton width="40%" height={24} />
          <Skeleton width="100%" height={280} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const pinData = (data?.pinsByStatus || []).map((item: any) => ({
    name: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "Unknown",
    value: item.count,
    fill: PIN_STATUS_COLORS[item.status] || "#94a3b8",
  }));

  return (
    <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Inventory - PIN Status
        </Typography>
        {pinData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={pinData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
              <Tooltip
                formatter={(value: any) => [formatNumber(value), "PINs"]}
                contentStyle={{ borderRadius: 8, fontSize: 13 }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={45}>
                {pinData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography color="text.secondary">No inventory data</Typography>
          </Box>
        )}
        {data?.lowStockProducts && data.lowStockProducts.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#fff3e0", borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={600} color="warning.dark">
              Low Stock Alert: {data.lowStockProducts.length} products with low inventory
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default InventoryHealthChart;
